import React from 'react';
import { withStyles, WithStyles, createStyles } from '@material-ui/core';
import { StickyContainer, Sticky } from 'react-sticky';
import throttle from 'lodash-es/throttle';

import { ShareButtonsContainer as ShareButtons } from 'ha/modules/Contentful/containers/ShareButtons';
import { RelatedArticles } from 'ha/modules/Contentful/components/RelatedArticles';
import { Grid, units } from '@ha/dsl';

import { BlogCard } from './BlogCard';
import { BlogForm } from './BlogForm';
import { CTAFindRoom } from './CTAFindRoom';
import {
  HermesData,
  BlogForm as BlogFormType,
  BlogCard as BlogCardType,
} from '../types';
import { Content } from './Content';
import { isArticleUpdated } from '../utils/isArticleUpdated';

export const styles = () =>
  createStyles({
    leftColumn: {
      paddingRight: units.rem(7.5),
    },
    rightColumn: {
      paddingLeft: units.rem(7.5),
    },
    shareButtons: {
      width: 'auto',
    },
    sticky: {
      paddingTop: units.rem(2.5),
    },
  });

interface Props {
  data: HermesData;
  isCTADisabled: boolean;
}

export const LayoutInternal: React.FC<Props & WithStyles<typeof styles>> = ({
  data,
  isCTADisabled,
  classes,
}) => {
  const {
    title,
    content,
    author,
    relatedArticles,
    city,
    country,
    ctaType,
    cta,
    createdAt,
    updatedAt,
    hideReadingTime,
    topicCluster,
  } = data;

  const [isSideBlockVisible, setIsSideBlockVisible] = React.useState(false);

  const handleScroll = throttle(() => {
    if (
      window.scrollY >=
        /* since footer and CTA block take a lot of space, we render block once 3rd is passed */
        document.body.offsetHeight / 3 &&
      !isSideBlockVisible
    ) {
      setIsSideBlockVisible(true);
    }
  }, 500);

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <StickyContainer>
      <Grid container data-test-locator="Blog Layout">
        <Grid container>
          <Grid
            item
            lg={2}
            data-test-locator="Blog Layout LeftColumn"
            className={classes.leftColumn}
          >
            <Sticky>
              {({ style, isSticky }) => (
                <div
                  style={{ ...style, display: isSticky ? 'initial' : 'none' }}
                >
                  <Grid container justify="flex-end" className={classes.sticky}>
                    <ShareButtons
                      title={title}
                      wrapperClassname={classes.shareButtons}
                      data-test-locator="Blog Layout ShareButtons"
                    />
                  </Grid>
                </div>
              )}
            </Sticky>
          </Grid>
          <Grid item lg={6}>
            <Content
              author={author}
              content={content}
              city={city}
              country={country}
              isUpdated={isArticleUpdated(createdAt, updatedAt)}
              hideReadingTime={hideReadingTime}
              topicCluster={topicCluster}
              data-test-locator="Blog Layout Content"
            />

            {relatedArticles && relatedArticles.length > 0 && (
              <RelatedArticles
                articles={relatedArticles}
                data-test-locator="Blog Layout RelatedArticles"
              />
            )}
          </Grid>
          {!isCTADisabled && (
            <Grid
              item
              lg={4}
              data-test-locator="Blog Layout RightColumn"
              className={classes.rightColumn}
            >
              <Sticky>
                {({ style, isSticky }) => (
                  <div
                    style={{
                      ...style,
                      display:
                        isSticky && isSideBlockVisible ? 'initial' : 'none',
                    }}
                  >
                    <Grid container justify="center" className={classes.sticky}>
                      <Grid item xs>
                        {ctaType === 'card' && cta && (
                          <BlogCard {...(cta as BlogCardType)} />
                        )}
                        {ctaType === 'form' && cta && (
                          <BlogForm {...(cta as BlogFormType)} />
                        )}
                        {!ctaType && city && country && (
                          <CTAFindRoom
                            city={city}
                            country={country}
                            data-test-locator="Blog Layout CTAFindRoom"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </div>
                )}
              </Sticky>
            </Grid>
          )}
        </Grid>
      </Grid>
    </StickyContainer>
  );
};

const Layout = withStyles(styles)(LayoutInternal);
export { Layout };
